import {
  FloatingFormItem,
  InputDecimal,
  SaveButton,
} from '@/components/_common';
import {
  useCrmMatrixSettings,
  useCrmMatrixSettingsUpdate,
} from '@/core/domain/enterprise/MatrixCrmSettings/MatrixCrmSettings.hooks';
import {
  CrmMatrixFormValues,
  CrmMatrixSettingsUpdate,
  MonthOrLifeTime,
} from '@/core/domain/enterprise/MatrixCrmSettings/MatrixCrmSettings.types';
import { useWindowSize } from '@/hooks';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Tooltip,
  Typography,
} from 'antd';
const { Title } = Typography;
import { useEffect } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useIntl } from 'react-intl';

const CrmSettingsForm = () => {
  const intl = useIntl();
  const [form] = Form.useForm<CrmMatrixFormValues>();
  const monthOrLifeTime = Form.useWatch('monthOrLifeTime', form);

  const { width } = useWindowSize();

  const { data } = useCrmMatrixSettings();
  const updateMutation = useCrmMatrixSettingsUpdate();

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
    }
  }, [data]);

  const handleUpdateCrmSettings = async () => {
    const values = await form.validateFields();

    if (data) {
      delete data.matrixId;
      const obj: CrmMatrixSettingsUpdate = {
        ...data,
        ...values,
      };

      await updateMutation.mutateAsync(obj);
    }
  };

  return (
    <>
      <div className="page-header">
        <Title level={4}>Configurações do CRM</Title>

        {!(width < 992) && (
          <SaveButton
            loading={updateMutation.isLoading}
            onClick={handleUpdateCrmSettings}
          >
            {intl.formatMessage({
              id: 'commons.form.button.save',
            })}
          </SaveButton>
        )}
      </div>

      <Form form={form} style={{ marginTop: '24px' }}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Form.Item name="monthOrLifeTime">
              <Radio.Group optionType="button" buttonStyle="solid" size="large">
                <Radio value={MonthOrLifeTime.kmOrLifetime}>KM/Vida Util</Radio>
                <Radio value={MonthOrLifeTime.month}>Meses</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {monthOrLifeTime === MonthOrLifeTime.month && (
            <Col span={24} lg={12} xxl={6}>
              <FloatingFormItem
                name="nextReviewMonth"
                label="Próximas revisões(meses)"
              >
                <InputDecimal fractions={0} />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24}>
            <Divider orientation="left">
              E-mail de resposta{' '}
              <Tooltip title="Ao enviar e-mails automáticos, o nome do remetente será exibido como identificação, e as respostas serão direcionadas para o e-mail configurado, mesmo que o envio seja feito a partir de crm@ultracar.com.br.">
                <FaInfoCircle
                  size={16}
                  style={{ marginLeft: 8, cursor: 'pointer' }}
                />
              </Tooltip>
            </Divider>
          </Col>

          <Col span={24} lg={12}>
            <FloatingFormItem name="senderName" label="Nome do remetente">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={12}>
            <FloatingFormItem
              name="replyMail"
              label="E-mail de resposta"
              rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({
                    id: 'commons.form.errors.invalidEmail',
                  }),
                },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Divider orientation="left">
              Selecione as notificações a serem enviadas de forma automática
            </Divider>
          </Col>

          <Col style={{ marginTop: -2 }}>
            <Row gutter={[24, 24]}>
              <Col>
                <Form.Item
                  name="birthdayAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Aniversário</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  name="futureServicesAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Serviços futuros</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  name="nextReviewsAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Próximas revisões</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  name="customerWithoutMovementAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Clientes sem movimento</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  name="satisfactionSurveyAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Pesquisa de satisfação</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  name="recoveryDebtsAutomaticSend"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Cobrança de dívidas</Checkbox>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item name="isSmtp" valuePropName="checked" noStyle>
                  <Checkbox>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Enviar emails de CRM via SMTP{' '}
                      <Tooltip title="Esta opção é utilizada apenas para envios manuais do CRM. Para envios automáticos, será usado exclusivamente o SendGrid. O envio via SMTP está sujeito aos limites diários ou mensais definidos pelo provedor do e-mail cadastrado.">
                        <FaInfoCircle
                          size={16}
                          style={{ marginLeft: 8, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </div>
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      {width < 992 && (
        <SaveButton
          block
          loading={updateMutation.isLoading}
          onClick={handleUpdateCrmSettings}
        >
          {intl.formatMessage({
            id: 'commons.form.button.save',
          })}
        </SaveButton>
      )}
    </>
  );
};

export default CrmSettingsForm;
